import { KeyboardEvent, ReactNode } from "react";

interface TagCloudItem {
  id: string;
  label: string;
}

interface DefaultTagProps<T extends TagCloudItem> {
  item: T;
  isSelected: boolean;
  selectedColor?: string;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

const DefaultTag = <T extends TagCloudItem>({
  item,
  isSelected,
  selectedColor = "rgba(124, 135, 194, 0.25)",
  onClick,
  onKeyDown,
}: DefaultTagProps<T>) => {
  return (
    <button
      className={`
        flex items-center justify-center rounded-full px-3 py-2 text-nearly-black shadow-subtle
        outline-none transition-all duration-200
        ${isSelected ? "bg-brand-tint" : "bg-white/40"}
      `}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
    >
      {item.label}
    </button>
  );
};

interface TagCloudPickerProps<T extends TagCloudItem> {
  items: T[];
  selectedIds: string[];
  onSelectionChangeRequest: (selectedItems: T[]) => void;
  selectedColor?: string;
  renderTag?: (props: DefaultTagProps<T>) => ReactNode;
}

export const TagCloudPicker = <T extends TagCloudItem>({
  items,
  selectedIds,
  onSelectionChangeRequest,
  selectedColor,
  renderTag = DefaultTag,
}: TagCloudPickerProps<T>) => {
  const handleTagClick = (item: T) => {
    const isCurrentlySelected = selectedIds.includes(item.id);
    let newSelectedItems: T[];

    if (isCurrentlySelected) {
      newSelectedItems = items.filter(
        (i) => selectedIds.includes(i.id) && i.id !== item.id
      );
    } else {
      newSelectedItems = items.filter(
        (i) => selectedIds.includes(i.id) || i.id === item.id
      );
    }

    onSelectionChangeRequest(newSelectedItems);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, item: T) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleTagClick(item);
    }
  };

  return (
    <div className="flex flex-wrap gap-2.5">
      {items.map((item) => {
        const isSelected = selectedIds.includes(item.id);

        return (
          <div key={item.id}>
            {renderTag({
              item,
              isSelected,
              selectedColor,
              onClick: () => handleTagClick(item),
              onKeyDown: (event) => handleKeyDown(event, item),
            })}
          </div>
        );
      })}
    </div>
  );
};
